import type { Event, GasPayload, HeatIndexPayload } from "@safers/api";
import { EmptyState } from "@/components/EmptyState";
import { EmptyIcon } from "@/components/emptyIcons";
import {
  GAS_BOUNDS,
  GAS_FIELDS,
  HEAT_LEVEL_META,
  HEAT_STEPS,
  heatLevelFromSeverity,
  type HeatLevel,
} from "@/services";

const LEVELS: HeatLevel[] = ["SAFE", "INTEREST", "CAUTION", "WARNING", "DANGER"];

interface MeasureItem {
  key: string;
  label: string;
  name?: string;
  value: number | null;
  unit: string;
  bounds?: readonly number[];
}

interface Measure {
  causes: MeasureItem[];
  others: MeasureItem[];
  othersTitle: string;
}

function formatValue(item: MeasureItem): string {
  if (item.value == null) return "—";
  return `${item.value.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} ${item.unit}`;
}

function gasMeasure(payload: GasPayload): Measure {
  const causes = new Set(payload.alarmFields ?? []);
  const items = GAS_FIELDS.map((f) => ({ ...f, value: payload[f.key], bounds: GAS_BOUNDS[f.key] }));
  return {
    causes: items.filter((item) => causes.has(item.key)),
    others: items.filter((item) => !causes.has(item.key)),
    othersTitle: "다른 측정값",
  };
}

function heatMeasure(payload: HeatIndexPayload): Measure {
  return {
    causes: [
      {
        key: "apparentTemperature",
        label: "체감온도",
        value: payload.apparentTemperature,
        unit: "℃",
        bounds: HEAT_STEPS.map((step) => step.from),
      },
    ],
    others: [
      { key: "temperature", label: "온도", value: payload.temperature, unit: "℃" },
      { key: "humidity", label: "습도", value: payload.humidity, unit: "%" },
    ],
    othersTitle: "계산에 쓰인 값",
  };
}

function measureOf(event: Event): Measure | null {
  if (event.payload?.kind === "gas") return gasMeasure(event.payload as unknown as GasPayload);
  if (event.payload?.kind === "heat-index") {
    return heatMeasure(event.payload as unknown as HeatIndexPayload);
  }
  return null;
}

const LEVEL_GRADIENT = `linear-gradient(to right, ${LEVELS.map(
  (lv, i) => `${HEAT_LEVEL_META[lv].fill} ${((i + 0.5) / LEVELS.length) * 100}%`
).join(", ")})`;

const boundaryLeft = (index: number) => `${((index + 1) / LEVELS.length) * 100}%`;

function markerPosition(current: number, value: number | null, bounds?: readonly number[]) {
  const [first, second] = bounds ?? [];
  const last = bounds?.at(-1);
  if (value == null || !bounds || bounds.length !== LEVELS.length - 1) return current + 0.5;
  if (first == null || second == null || last == null) return current + 0.5;
  const width = second - first;
  const edges = [first - width, ...bounds, last + width];
  const from = edges[current] ?? first;
  const to = edges[current + 1] ?? last;
  if (to === from || value < Math.min(from, to) || value > Math.max(from, to)) return current + 0.5;
  return current + Math.min(0.9, Math.max(0.1, (value - from) / (to - from)));
}

interface LevelLineProps {
  level: HeatLevel;
  item?: MeasureItem;
}

function LevelLine({ level, item }: LevelLineProps) {
  const current = LEVELS.indexOf(level);
  const meta = HEAT_LEVEL_META[level];
  const left = `${(markerPosition(current, item?.value ?? null, item?.bounds) / LEVELS.length) * 100}%`;
  return (
    <div className="mt-2">
      <div className="relative h-9">
        <div
          className="absolute bottom-0 flex -translate-x-1/2 flex-col items-center"
          style={{ left }}
        >
          {item && (
            <span
              className="whitespace-nowrap text-[0.8125rem] font-bold tabular-nums"
              style={{ color: meta.panelFg }}
            >
              {formatValue(item)}
            </span>
          )}
          <span
            aria-hidden="true"
            className="h-0 w-0 border-x-[0.375rem] border-t-[0.5rem] border-x-transparent"
            style={{ borderTopColor: meta.panelFg }}
          />
        </div>
      </div>
      <div className="relative mt-1 h-1.5 rounded-full" style={{ backgroundImage: LEVEL_GRADIENT }}>
        {item?.bounds?.map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className="absolute -top-0.5 h-2.5 w-0.5 -translate-x-1/2 rounded-full bg-white"
            style={{ left: boundaryLeft(i) }}
          />
        ))}
      </div>
      {item?.bounds && (
        <div className="relative mt-1 h-4 text-[0.6875rem] tabular-nums text-neutral-500">
          {item.bounds.map((bound, i) => (
            <span
              key={bound}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: boundaryLeft(i) }}
            >
              {bound}
              {item.unit}
            </span>
          ))}
        </div>
      )}
      <div className="mt-1.5 flex">
        {LEVELS.map((lv, i) => (
          <span
            key={lv}
            className={`flex-1 text-center text-[0.6875rem] ${
              i === current ? "font-bold text-neutral-900" : "text-neutral-400"
            }`}
          >
            {HEAT_LEVEL_META[lv].label}
          </span>
        ))}
      </div>
    </div>
  );
}

function CauseCard({ item, level }: { item: MeasureItem; level: HeatLevel }) {
  const meta = HEAT_LEVEL_META[level];
  return (
    <div
      className="rounded-xl border p-4"
      style={{ backgroundColor: meta.panelBg, borderColor: meta.chipBg }}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[0.875rem] font-semibold text-neutral-900">
          {item.label}
          {item.name && <span className="ml-1 font-normal text-neutral-500">{item.name}</span>}
        </p>
        <span
          className="flex-none rounded px-1.5 py-0.5 text-[0.75rem] font-bold"
          style={{ backgroundColor: meta.chipBg, color: meta.chipFg }}
        >
          {meta.label}
        </span>
      </div>
      <LevelLine level={level} item={item} />
    </div>
  );
}

function ValueList({ title, items }: { title: string; items: MeasureItem[] }) {
  if (items.length === 0) return null;
  return (
    <section>
      <h4 className="mb-1.5 text-[0.75rem] font-semibold text-neutral-500">{title}</h4>
      <dl
        className="grid gap-2 rounded-lg border border-neutral-200 px-3 py-2"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((item) => (
          <div key={item.key} className="min-w-0">
            <dt className="truncate text-[0.6875rem] text-neutral-500">{item.label}</dt>
            <dd className="truncate text-[0.8125rem] font-semibold tabular-nums text-neutral-800">
              {formatValue(item)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function EventMeasure({ event }: { event: Event }) {
  const measure = measureOf(event);
  if (!measure) {
    return (
      <div className="flex min-h-[8rem] rounded-lg border border-neutral-200">
        <EmptyState icon={EmptyIcon.device} title="측정값 없음" />
      </div>
    );
  }

  const level = heatLevelFromSeverity(event.severity);
  const { causes, others, othersTitle } = measure;

  return (
    <div className="flex flex-col gap-3">
      {causes.length > 0 ? (
        <div className={`grid gap-2 ${causes.length > 1 ? "sm:grid-cols-2" : ""}`}>
          {causes.map((item) => (
            <CauseCard key={item.key} item={item} level={level} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-neutral-200 p-4">
          <p className="text-[0.8125rem] text-neutral-600">
            장비가 경보 원인 항목을 보내지 않았습니다.
          </p>
          <LevelLine level={level} />
        </div>
      )}
      <ValueList title={causes.length > 0 ? othersTitle : "측정값"} items={others} />
    </div>
  );
}
